import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from 'features/dashboard';
import AiBlogWriter from 'features/ai-blog-writer';
import Auth from 'features/auth';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store, { RootState } from 'store';
import { loadUserFromStorage } from 'store/slices/authSlice';
import { Check, CircleAlert } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import PodCast from 'features/podcast';
import TextToImageGenerator from 'features/text-to-image-generator';
import SocialMedia from 'features/social-media';
import TwitterAuthState from 'features/social-media/pages/TwitterAuthState';
import BotBuilder from 'features/bot-builder';
import StickerMaker from 'features/sticker-maker';
import Brandbook from 'features/brandbook';
import GifMaker from 'features/gif-maker';
import ChatWithDocs from 'features/chat-with-docs';
import LinkedInAuthState from 'features/social-media/pages/LinkedInAuthState';
import TextToSpeech from 'features/text-to-speech';
import FilesRoutes from 'features/files';
import EmailGenerator from 'features/email-generator';
import TextToLogoGenerator from 'features/text-to-logo';
import Transcript from 'features/transcript';
import UpscaleImage from 'features/upscale-image';
import ObjectRemover from 'features/object-remover';
import ObjectReplacer from 'features/object-replacer';
import MobileProfileSettings from 'components/ProfileSettings/MobileProfileSettings';
import NotFound from 'components/pages/NotFound';
import Maintainance from 'components/pages/Maintainance';
import ErrorBoundary from 'components/ErrorBoundary';
import SomethingWentWrong from 'components/pages/SomethingWentWrong';
import { RichTextEditorDemo } from 'components/tiptap/rich-text-editor';
import Layout from 'components/Layout';
import ImageToImage from 'features/image-to-image';
import ConnectGoogleCallback from 'components/ProfileSettings/ConnectGoogleCallback';
import ContactUs from 'features/contact-us/pages/ContactUs';
import ReactGA from 'react-ga4';

const GA_TRACKING_ID=process.env.REACT_APP_GA_TRACKING_ID;

const App: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [authChecked, setAuthChecked] = useState(false);

  
  useEffect(() => {
    const checkAuth = async () => {
      await dispatch(loadUserFromStorage());
      setAuthChecked(true);
    };

    checkAuth();
    // Initialize theme from localStorage
    const theme = localStorage.getItem('theme');
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [dispatch]);



  /**
   * Google Analytics Initialization
   */
  useEffect(() => {
    if (GA_TRACKING_ID) {
      ReactGA.initialize(GA_TRACKING_ID);
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    } else {
      console.warn('GA_TRACKING_ID is not defined');
    }
  }, []); 

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  if (!authChecked) {
    return (
      <div className="flex items-center bg-background text-text-primary justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route
            path="/tiptap-editor"
            element={
              <Layout>
                <div className="mx-auto w-full container flex flex-col justify-center items-center py-5">
                  <RichTextEditorDemo className="w-full rounded-xl" />
                </div>
              </Layout>
            }
          />
          <Route path="/ai-blog-writer/*" element={<AiBlogWriter />} />
          <Route path="/twitter/error" element={<TwitterAuthState />} />
          <Route path="/twitter/success" element={<TwitterAuthState />} />
          <Route path="/linkedin/error" element={<LinkedInAuthState />} />
          <Route path="/linkedin/success" element={<LinkedInAuthState />} />
          <Route path="bot-builder/*" element={<BotBuilder />} />
          <Route path="/text-to-image/*" element={<TextToImageGenerator />} />
          <Route path="/text-to-logo/*" element={<TextToLogoGenerator />} />
          <Route path="/ai-podcast-generator/*" element={<PodCast />} />
          <Route path="/social-media/*" element={<SocialMedia />} />
          <Route path="/gif-maker/*" element={<GifMaker />} />
          <Route path="/chat-with-docs/*" element={<ChatWithDocs />} />
          <Route path="/sticker-maker/*" element={<NotFound />} />
          <Route path="/text-to-speech/*" element={<TextToSpeech />} />
          <Route path="/brandbook/*" element={<Brandbook />} />
          <Route path="/email-generator/*" element={<EmailGenerator />} />
          <Route path="/object-remover/*" element={<ObjectRemover />} />
          <Route path="/object-replacer/*" element={<ObjectReplacer />} />
          <Route path="/upscale-image/*" element={<UpscaleImage />} />
          <Route path="/image-to-image/*" element={<ImageToImage />} />
          <Route path="/auth/*" element={<Auth />} />
          {isAuthenticated ? (
            <Route path="/files/*" element={<FilesRoutes />} />
          ) : (
            <Route path="/files/*" element={<Navigate to="/auth/login" />} />
          )}
          <Route path="/transcript/*" element={<Transcript />} />
          <Route
            path="/profile-settings/mobile"
            element={<MobileProfileSettings />}
          />
          <Route path="/maintainance" element={<Maintainance />} />
          <Route path="/wrong" element={<SomethingWentWrong />} />
          <Route path="/auth/connect-google-callback" element={<ConnectGoogleCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster
          position="bottom-center"
          toastOptions={{
            success: {
              style: {
                backgroundColor: '#30A46C',
                color: '#ffffff',
                minWidth: '400px',
                maxWidth: '500px',
                padding: '10px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              icon: <Check size={20} />,
            },
            error: {
              style: {
                backgroundColor: '#E5484D',
                minWidth: '400px',
                maxWidth: '500px',
                color: '#ffffff',
                padding: '10px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              icon: <CircleAlert size={20} />,
            },
          }}
        />
      </ErrorBoundary>
    </Provider>
  );
};

export default App;
